import { ReactNode, memo, useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  AppstoreOutlined,
  FileTextOutlined,
  LikeOutlined,
  NotificationOutlined,
  SafetyCertificateOutlined,
  TeamOutlined,
  UICard,
  UIDrawer,
  UIMenu,
  UIMenuItemType,
  UIMenuProps,
  UISpin,
} from "ui";
import { LIMIT_MENU_ITEMS, ROUTE_URLS, SUBMENU_TYPES } from "../../constants/consts";
import useSWR from "swr";
import {
  setHierarchyCategoryData,
  setHierarchyCategoryError,
} from "../../store/slices/MenuCategoriesSlice";
import { getCategories } from "../../api/categories";
import {
  getUserId,
} from "../../api/sessionManagement";
import Logo from "../../images/rde_standart.png";
import "./SideBar.sass";
import { useDispatch, useSelector } from "react-redux";
import { Trans, useTranslation } from "react-i18next";

const CATEGORY_CACHE_KEY_PREFIX = "b2b_portal:menu_categories:v1";
const CATEGORY_CACHE_TTL_MS = 15 * 60 * 1000;

type CategoryNode = {
  id: number;
  name: string;
  image_url: string;
  child_ids: CategoryNode[];
  is_has_products: boolean;
};

type NavigationTreeItem = {
  key: string;
  label: ReactNode;
  path?: string;
  state?: Record<string, unknown>;
  disabled?: boolean;
  children?: NavigationTreeItem[];
};

type CategoriesCachePayload = {
  timestamp: number;
  data: CategoryNode[];
};

const normalizeLanguageCode = (language: string) =>
  (language || "en").toLowerCase().split("-")[0];

const resolveMainMenuByPath = (pathname: string) => {
  if (
    pathname.startsWith(ROUTE_URLS.ORDERS) ||
    pathname.startsWith(ROUTE_URLS.RETURNS)
  ) {
    return SUBMENU_TYPES.ORDERS;
  }

  if (
    pathname.startsWith(ROUTE_URLS.WARRANTY_TERMS) ||
    pathname.startsWith(ROUTE_URLS.WARRANTY_PERIODS) ||
    pathname.startsWith(ROUTE_URLS.WARRANTY_CASE) ||
    pathname.startsWith(ROUTE_URLS.WARRANTIES)
  ) {
    return SUBMENU_TYPES.GUARANTEE;
  }

  if (
    pathname.startsWith(ROUTE_URLS.QUESTIONARY) ||
    pathname.startsWith(ROUTE_URLS.CONTACT_INFORMATION) ||
    pathname.startsWith(ROUTE_URLS.TERM_SERVICE) ||
    pathname.startsWith(ROUTE_URLS.PORTAL_RULES)
  ) {
    return SUBMENU_TYPES.QUESTIONARY;
  }

  if (pathname.startsWith(ROUTE_URLS.CATALOG)) {
    return SUBMENU_TYPES.CATALOG;
  }

  return "";
};

const mapTreeToMenuItems = (items: NavigationTreeItem[]): UIMenuItemType[] =>
  items.map((item) => ({
    key: item.key,
    label: item.label,
    disabled: item.disabled,
    children: item.children ? mapTreeToMenuItems(item.children) : undefined,
  }));

const collectActions = (
  items: NavigationTreeItem[],
  onLeafSelect: (path: string, state?: Record<string, unknown>) => void
) => {
  const actions = new Map<string, () => void>();

  const walk = (nodeItems: NavigationTreeItem[]) => {
    nodeItems.forEach((item) => {
      if (item.path) {
        actions.set(item.key, () => onLeafSelect(item.path as string, item.state));
      }

      if (item.children?.length) {
        walk(item.children);
      }
    });
  };

  walk(items);
  return actions;
};

const getCategoryCacheKey = (language: string, userId: number | null) =>
  `${CATEGORY_CACHE_KEY_PREFIX}:u${userId ?? "anon"}:${normalizeLanguageCode(language)}`;

const getCachedCategories = (
  language: string,
  userId: number | null
): CategoryNode[] | null => {
  if (typeof window === "undefined") {
    return null;
  }

  try {
    const cacheKey = getCategoryCacheKey(language, userId);
    const raw =
      window.localStorage.getItem(cacheKey) ??
      window.sessionStorage.getItem(cacheKey);
    if (!raw) {
      return null;
    }

    const parsed = JSON.parse(raw) as CategoriesCachePayload;
    const isFresh = Date.now() - parsed.timestamp < CATEGORY_CACHE_TTL_MS;
    return isFresh && Array.isArray(parsed.data) ? parsed.data : null;
  } catch {
    return null;
  }
};

const setCachedCategories = (
  language: string,
  userId: number | null,
  data: CategoryNode[]
) => {
  if (typeof window === "undefined") {
    return;
  }

  try {
    const payload: CategoriesCachePayload = {
      timestamp: Date.now(),
      data,
    };
    const cacheKey = getCategoryCacheKey(language, userId);
    const serialized = JSON.stringify(payload);

    // Keep localStorage for warm start across sessions and sessionStorage as fallback.
    window.localStorage.setItem(cacheKey, serialized);
    window.sessionStorage.setItem(cacheKey, serialized);
  } catch {
    // Ignore cache write errors to avoid affecting menu behavior.
  }
};

const mapCategoriesTreeLazy = (
  categories: CategoryNode[] = [],
  openedKeys: Set<string>
): NavigationTreeItem[] =>
  categories
    .filter((category) => category.is_has_products)
    .map((category) => {
      const key = `catalog-${category.id}`;
      const childrenSource = (category.child_ids ?? []).filter(
        (child) => child.is_has_products
      );

      if (!childrenSource.length) {
        return {
          key,
          label: category.name,
          path: `${ROUTE_URLS.CATALOG}/${category.id}`,
        };
      }

      const children = openedKeys.has(key)
        ? mapCategoriesTreeLazy(childrenSource, openedKeys)
        : [{ key: `${key}__placeholder`, label: "", disabled: true }];

      return {
        key,
        label: category.name,
        children,
      };
    });

export const MainSidebar = memo(() => {
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { pathname } = useLocation();
  const categories = useSelector(
    (state: any) => (state.hierarchyCategory.data ?? []) as CategoryNode[]
  );

  const [submenuType, setSubmenuType] = useState<SUBMENU_TYPES | null>(null);
  const [openSubmenuKeys, setOpenSubmenuKeys] = useState<string[]>([]);
  const [selectedMainKey, setSelectedMainKey] = useState<string>("");
  const currentUserId = getUserId();
  const currentLanguage = useMemo(
    () => normalizeLanguageCode(i18n.resolvedLanguage || i18n.language || "en"),
    [i18n.language, i18n.resolvedLanguage]
  );
  const cachedCategories = useMemo(
    () => getCachedCategories(currentLanguage, currentUserId) ?? [],
    [currentLanguage, currentUserId]
  );

  const shouldFetchCategories = !cachedCategories.length;
  const { data, error: swrError, isValidating } = useSWR(
    shouldFetchCategories ? `category:${currentLanguage}` : null,
    () => getCategories(),
    {
      revalidateOnFocus: false,
    }
  );

  useEffect(() => {
    dispatch(setHierarchyCategoryData(cachedCategories));
  }, [cachedCategories, dispatch]);

  useEffect(() => {
    if (data) {
      const fetchedData = (data?.data?.data ?? []) as CategoryNode[];
      dispatch(setHierarchyCategoryData(fetchedData));
      setCachedCategories(currentLanguage, currentUserId, fetchedData);
    } else if (swrError) {
      dispatch(setHierarchyCategoryError(swrError.message));
    }
  }, [data, swrError, dispatch, currentLanguage, currentUserId]);

  useEffect(() => {
    setSelectedMainKey(resolveMainMenuByPath(pathname));
  }, [pathname]);

  useEffect(
    () => () => {
      document.body.style.overflow = "initial";
    },
    []
  );

  const closeAllCategory = () => {
    setSubmenuType(null);
    setOpenSubmenuKeys([]);
    document.body.style.overflow = "initial";
  };

  const openSubCategory = (type: SUBMENU_TYPES) => {
    setSubmenuType(type);
    setOpenSubmenuKeys([]);
    document.body.style.overflow = "hidden";
  };

  const handleMainMenuClick: NonNullable<UIMenuProps["onClick"]> = ({ key }) => {
    const nextKey = String(key);

    switch (nextKey) {
      case SUBMENU_TYPES.CATALOG:
      case SUBMENU_TYPES.ORDERS:
      case SUBMENU_TYPES.QUESTIONARY:
      case SUBMENU_TYPES.GUARANTEE:
        setSelectedMainKey(nextKey);
        openSubCategory(nextKey as SUBMENU_TYPES);
        return;
      case "services":
      case "recommended":
        closeAllCategory();
        setSelectedMainKey(nextKey);
        navigate(ROUTE_URLS.HOME);
        return;
      default:
        return;
    }
  };

  const menuItems: UIMenuItemType[] = [
    {
      key: SUBMENU_TYPES.CATALOG,
      icon: <AppstoreOutlined />,
      label: <Trans i18nKey="catalog" />,
    },
    {
      key: SUBMENU_TYPES.ORDERS,
      icon: <FileTextOutlined />,
      label: <Trans i18nKey="orders" />,
    },
    ...(!LIMIT_MENU_ITEMS
      ? [
          {
            key: SUBMENU_TYPES.GUARANTEE,
            icon: <SafetyCertificateOutlined />,
            label: <Trans i18nKey="guarantee" />,
          },
          {
            key: "services",
            icon: <TeamOutlined />,
            label: <Trans i18nKey="services" />,
          },
          {
            key: "recommended",
            icon: <LikeOutlined />,
            label: <Trans i18nKey="recommended" />,
          },
        ]
      : []),
    {
      key: SUBMENU_TYPES.QUESTIONARY,
      icon: <NotificationOutlined />,
      label: <Trans i18nKey="information" />,
    },
  ];

  const catalogTree = useMemo<NavigationTreeItem[]>(() => {
    if (!categories.length) {
      return [];
    }

    const openedKeys = new Set(openSubmenuKeys);
    return mapCategoriesTreeLazy(categories, openedKeys);
  }, [categories, openSubmenuKeys]);

  const submenuTree = useMemo<NavigationTreeItem[]>(() => {
    if (!submenuType) {
      return [];
    }

    switch (submenuType) {
      case SUBMENU_TYPES.CATALOG:
        return catalogTree;
      case SUBMENU_TYPES.ORDERS:
        return [
          {
            key: "orders-all",
            label: <Trans i18nKey="orders" />,
            path: ROUTE_URLS.ORDERS,
            state: { isSetInitial: true },
          },
          ...(!LIMIT_MENU_ITEMS
            ? [
                {
                  key: "orders-returns",
                  label: <Trans i18nKey="productReturns" />,
                  path: ROUTE_URLS.RETURNS,
                },
                {
                  key: "orders-delivery-issues",
                  label: <Trans i18nKey="deliveryIssues" />,
                  disabled: true,
                },
              ]
            : []),
        ];
      case SUBMENU_TYPES.GUARANTEE:
        return [
          {
            key: "guarantee-conditions",
            label: <Trans i18nKey="guarantee_conditions" />,
            path: ROUTE_URLS.WARRANTY_TERMS,
          },
          {
            key: "guarantee-claims",
            label: <Trans i18nKey="claims" />,
            path: ROUTE_URLS.WARRANTIES,
          },
          {
            key: "guarantee-periods",
            label: <Trans i18nKey="warranty_periods" />,
            path: ROUTE_URLS.WARRANTY_PERIODS,
          },
          {
            key: "guarantee-case",
            label: <Trans i18nKey="warranty_case_instructions" />,
            path: ROUTE_URLS.WARRANTY_CASE,
          },
        ];
      case SUBMENU_TYPES.QUESTIONARY:
        return [
          {
            key: "questionary-partner",
            label: <Trans i18nKey="partner_data" />,
            path: ROUTE_URLS.QUESTIONARY,
          },
          {
            key: "questionary-mk",
            label: <Trans i18nKey="mk_trade_data" />,
            children: [
              {
                key: "questionary-contact",
                label: <Trans i18nKey="contact_information" />,
                path: ROUTE_URLS.CONTACT_INFORMATION,
              },
              {
                key: "questionary-terms",
                label: <Trans i18nKey="user_agreement" />,
                path: ROUTE_URLS.TERM_SERVICE,
              },
              {
                key: "questionary-portal-rules",
                label: <Trans i18nKey="portal_rules" />,
                path: ROUTE_URLS.PORTAL_RULES,
              },
            ],
          },
        ];
      default:
        return [];
    }
  }, [submenuType, catalogTree]);

  const submenuItems = useMemo<UIMenuItemType[]>(
    () =>
      submenuTree.length
        ? mapTreeToMenuItems(submenuTree)
        : [{ key: "empty", label: <Trans i18nKey="noDataToDisplay" />, disabled: true }],
    [submenuTree]
  );

  const isCatalogLoading =
    submenuType === SUBMENU_TYPES.CATALOG &&
    shouldFetchCategories &&
    !categories.length &&
    !swrError &&
    (isValidating || !data);

  const submenuActions = useMemo(
    () =>
      collectActions(submenuTree, (path, state) => {
        if (state) {
          navigate(path, { state });
          return;
        }

        navigate(path);
      }),
    [navigate, submenuTree]
  );

  const handleSubmenuClick: NonNullable<UIMenuProps["onClick"]> = ({ key }) => {
    const action = submenuActions.get(String(key));
    if (!action) {
      return;
    }

    action();
    closeAllCategory();
  };

  const handleSubmenuOpenChange: NonNullable<UIMenuProps["onOpenChange"]> = (
    keys
  ) => {
    setOpenSubmenuKeys(keys.map((key) => String(key)));
  };

  return (
    <>
      <aside className="mainSidebar">
        <UICard className="mainSidebar__card" bordered={false}>
          <Link
            to={ROUTE_URLS.HOME}
            className="cremaNavbar__logoLink mainSidebar__logoLink"
            aria-label="Go to home page"
          >
            <img alt="RDE logo" src={Logo} className="cremaNavbar__logo mainSidebar__logo" />
          </Link>
          <div className="mainSidebar__userDivider" />
          <UIMenu
            mode="inline"
            className="mainSidebar__menu"
            items={menuItems}
            onClick={handleMainMenuClick}
            selectedKeys={selectedMainKey ? [selectedMainKey] : []}
          />
        </UICard>
      </aside>

      <UIDrawer
        rootClassName="mainSidebar__drawer"
        title={
          submenuType ? (
            <span className="mainSidebar__drawerTitle">
              <Trans i18nKey={submenuType} />
            </span>
          ) : null
        }
        placement="left"
        width={340}
        open={Boolean(submenuType)}
        onClose={closeAllCategory}
        destroyOnClose
      >
        {isCatalogLoading ? (
          <div className="mainSidebar__submenuLoader">
            <UISpin />
          </div>
        ) : (
          <UIMenu
            mode="inline"
            className="mainSidebar__submenu"
            items={submenuItems}
            onClick={handleSubmenuClick}
            openKeys={openSubmenuKeys}
            onOpenChange={handleSubmenuOpenChange}
          />
        )}
      </UIDrawer>
    </>
  );
});
